/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router';
import { Card, List } from 'semantic-ui-react';

import DiseaseApi from 'api/DiseaseApi';
import { HistoryPanelFilterOptionsValue } from 'models/FilterOptions';
import { DisableTRANSPAR } from 'utils/constants';
import { sxtheme } from 'utils/cssHelpers';
import { formatRelativeDate } from 'utils/dateTimeHelpers';
import { MapAggregationCat2Request } from 'utils/modelHelpers';
import { isMobile } from 'utils/responsive';

import { zoomToLocation } from 'map/map.saga';

import { ListLabelsHeader, SectionHeader } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';
import BdButton from 'components/_controls/BdButton/BdButton';
import { BdTooltip } from 'components/_controls/BdTooltip';
import { ViewPanel } from 'components/_controls/ViewPanel';
import { UnderstandingCaseAndDeathReporting } from 'components/_static/UnderstandingCaseAndDeathReporting';
import { Accordian } from 'components/Accordian';
import { RiskOfExportation, RiskOfImportation } from 'components/RisksProjectionCard';
import {
  AirportExportationItem,
  AirportImportationItem,
} from 'components/SidebarView/EventDetailPanel/AirportItem';
import OutbreakSurveillanceOverall from 'components/SidebarView/EventDetailPanel/OutbreakSurveillanceOverall';
import ReferenceList from 'components/SidebarView/EventDetailPanel/ReferenceList';
import {
  PopupAirportExport,
  PopupAirportImport,
  PopupTotalExport,
  PopupTotalImport,
} from 'components/TransparencyTooltips';

import diseaseActivityDetailsSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityDetails.selectors';
import { setDiseaseActivityUpdatesFilter } from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.slice';
import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { closePanel, openPanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import { minimizePanel, PanelType } from 'diseaseActivityPanels/diseaseActivityPanels.slice';
import locationsSelectors from 'locations/locations.selectors';
import metadataSelectors from 'metadata/metadata.selectors';

import GoogleTranslateLogoSvg from 'assets/google-translate-logo.svg';

import DiseaseActivityCard from './DiseaseActivityCards/DiseaseActivityCard';
import { RecentActivityCard } from './DiseaseActivityDetails/RecentActivityCard';
import { RiskCardForCountry } from './DiseaseActivityDetails/RiskCardForCountry';
import DiseaseActivityUpdates from './DiseaseActivityUpdates';
import ParametersPanel from './ParametersPanel';

const DiseaseActivityDetails: React.FC = () => {
  const dispatch = useDispatch();
  const isMobileDevice = isMobile(useBreakpointIndex());

  const appMetadata = useSelector(metadataSelectors.getMetadata);
  const timeframeStartDate = useSelector(
    diseaseActivitiesTimeFrameSelectors.timeframeStartDateAsDate
  );
  const isLoading = useSelector(diseaseActivityDetailsSelectors.isLoadingWrtPreviousPanel);
  const hasError = useSelector(diseaseActivityDetailsSelectors.hasError);
  const disactKey = useSelector(diseaseActivityPanelsSelectors.disacParams);
  const activePanel = useSelector(diseaseActivityPanelsSelectors.activePanel);
  const isHistorySelected = useSelector(
    diseaseActivityPanelsSelectors.isHistorySelectedFromActivityDetails
  );
  const selectedLocationName = useSelector(locationsSelectors.getSelectedLocationTitle);
  const userLocationIds = useSelector(locationsSelectors.getAllLocationIds);

  const countryName = useSelector(diseaseActivityDetailsSelectors.countryName);
  const eventTitle = useSelector(diseaseActivityDetailsSelectors.eventTitle);
  const eventStartDate = useSelector(diseaseActivityDetailsSelectors.eventStartDate);
  const lastUpdatedDate = useSelector(diseaseActivityDetailsSelectors.lastUpdatedDate);
  const caseCounts = useSelector(diseaseActivityDetailsSelectors.caseCounts);
  const eventLocations = useSelector(diseaseActivityDetailsSelectors.eventLocations);
  const importationRisk = useSelector(diseaseActivityDetailsSelectors.importationRisk);
  const exportationRisk = useSelector(diseaseActivityDetailsSelectors.exportationRisk);
  const airports = useSelector(diseaseActivityDetailsSelectors.airports);
  const sourceAirports = useSelector(diseaseActivityDetailsSelectors.sourceAirports);
  const destinationAirports = useSelector(diseaseActivityDetailsSelectors.destinationAirports);
  const articles = useSelector(diseaseActivityDetailsSelectors.articles);
  const calculationMetadata = useSelector(diseaseActivityDetailsSelectors.calculationMetadata);
  const firstActivityUpdateDate = useSelector(
    diseaseActivityDetailsSelectors.firstActivityUpdateDate
  );

  const { canClose, isMinimized, isOpen } = useSelector(
    diseaseActivityPanelsSelectors.getPanelVisibility(PanelType.DiseaseActivityDetailsPanel)
  );

  const onMinimize = (flag: boolean): void => {
    dispatch(minimizePanel(PanelType.DiseaseActivityDetailsPanel, flag));
  };

  const openHistoryPanel = (filter: HistoryPanelFilterOptionsValue, activityDate?: string) => {
    dispatch(setDiseaseActivityUpdatesFilter(filter));
    dispatch(
      openPanel({
        panel: 'DiseaseActivityUpdatesFromDetails',
        activityDate: activityDate,
      })
    );
  };

  const showPanel =
    (isMobileDevice && activePanel === PanelType.DiseaseActivityDetailsPanel) ||
    (!isMobileDevice && isOpen);

  return (
    <>
      {showPanel && (
        <ViewPanel
          title={
            isLoading
              ? 'Loading...'
              : eventTitle || (hasError ? 'Something went wrong' : 'Loading...')
          }
          subtitleMobile={selectedLocationName || 'Loading...'}
          isLoading={isLoading}
          hasError={hasError}
          canClose={canClose}
          onClose={() => dispatch(closePanel(PanelType.DiseaseActivityDetailsPanel))}
          isMinimized={isMinimized}
          onMinimize={onMinimize}
        >
          <>
            <div
              sx={{
                p: '16px',
                bg: sxtheme(t => t.colors.deepSea10),
              }}
            >
              {!isMobileDevice && eventLocations?.length > 0 && (
                <div sx={{ mb: '8px' }}>
                  <BdButton
                    type="tertiary"
                    data-testid="zoom-location"
                    onClick={() => dispatch(zoomToLocation())}
                    prefixIcon="icon-target"
                    text="Zoom to Location"
                    small
                  />
                </div>
              )}

              <Typography variant="caption" color="stone50">
                Last checked on {formatRelativeDate(lastUpdatedDate, 'AbsoluteDate')}
              </Typography>

              <DiseaseActivityCard
                isGlobal={false}
                countryName={countryName}
                totalCaseCounts={caseCounts}
                firstActivityUpdateDate={firstActivityUpdateDate}
                isFooterActive={isHistorySelected}
                onNeedApiGetDiseaseActivity={(startDate, endDate, aggregationCategory) =>
                  DiseaseApi.DiseaseController_GetTemporalActivity({
                    ...MapAggregationCat2Request[aggregationCategory],
                    diseaseId: disactKey.diseaseId,
                    speciesId: disactKey.speciesId,
                    countryId: disactKey.countryId,
                    includeProximalActivity: false,
                    activityStartDate: startDate ? startDate.toISOString() : undefined,
                    activityEndDate: endDate ? endDate.toISOString() : undefined,
                  })
                }
                onGoToActivityHistory={(date?: string) => openHistoryPanel('all-activity', date)}
              />

              <RecentActivityCard
                onGoToActivityHistory={(date?: string) =>
                  openHistoryPanel('with-updates-only', date)
                }
              />

              <RiskCardForCountry />
            </div>
            <Accordian expanded={false} title="Case Surveillance" sticky>
              <Accordian
                expanded={false}
                title="Understanding Case/Death Reporting"
                rounded
                sx={{ mb: '24px' }}
              >
                <UnderstandingCaseAndDeathReporting />
              </Accordian>
              <OutbreakSurveillanceOverall
                caseCounts={caseCounts}
                eventLocations={eventLocations}
                userLocationIds={userLocationIds}
              />
            </Accordian>

            <Accordian expanded={false} title="Risk of Importation" sticky>
              {!!importationRisk && (
                <>
                  <SectionHeader icon="icon-plane-import">Overall</SectionHeader>
                  <BdTooltip
                    className="transparency"
                    customPopup={
                      <PopupTotalImport
                        airports={airports}
                        eventStartDate={eventStartDate}
                        locationFullName={selectedLocationName}
                        appMetadata={appMetadata}
                      />
                    }
                    wide="very"
                    disabled={
                      DisableTRANSPAR ||
                      importationRisk.isModelNotRun ||
                      importationRisk.isModelRunning
                    }
                  >
                    <Card fluid className="borderless">
                      <RiskOfImportation
                        risk={importationRisk}
                        showCovidDisclaimerTooltip="if calculated"
                      />
                    </Card>
                  </BdTooltip>
                </>
              )}

              <SectionHeader>Airports Globally with &gt;1% Risk of Importation</SectionHeader>
              <ListLabelsHeader
                lhs={['Destination airport']}
                rhs={['Likelihood of case importation', 'Estimated case importations']}
              />
              <List className="xunpadded">
                {destinationAirports?.map(x => (
                  <List.Item key={x.id}>
                    <BdTooltip
                      className="transparency individual"
                      customPopup={
                        <PopupAirportImport
                          airport={x}
                          totalDestinationVolume={airports?.totalDestinationVolume}
                          totalSourceVolume={airports?.totalSourceVolume}
                          eventStartDate={eventStartDate}
                          appMetadata={appMetadata}
                        />
                      }
                      wide="very"
                      disabled={DisableTRANSPAR}
                    >
                      <AirportImportationItem airport={x} />
                    </BdTooltip>
                  </List.Item>
                )) || (
                  <Typography
                    variant="body2"
                    color="stone90"
                    sx={{ textAlign: 'center', fontStyle: 'italic' }}
                  >
                    {!importationRisk ||
                    importationRisk.isModelNotRun ||
                    exportationRisk.isModelRunning
                      ? 'No airports returned because risk was not calculated'
                      : 'No airports with >1% risk of importation'}
                  </Typography>
                )}
              </List>
            </Accordian>

            {!!exportationRisk && (
              <Accordian expanded={false} title="Risk of Exportation" sticky>
                <SectionHeader icon="icon-plane-export">Overall</SectionHeader>
                <BdTooltip
                  className="transparency"
                  customPopup={
                    <PopupTotalExport
                      airports={airports}
                      eventStartDate={eventStartDate}
                      calculationMetadata={calculationMetadata}
                      caseCounts={caseCounts}
                      eventTitle={eventTitle || 'Loading...'}
                      appMetadata={appMetadata}
                    />
                  }
                  wide="very"
                  disabled={
                    DisableTRANSPAR ||
                    exportationRisk.isModelNotRun ||
                    exportationRisk.isModelRunning ||
                    !calculationMetadata
                  }
                >
                  <Card fluid className="borderless">
                    <RiskOfExportation
                      risk={exportationRisk}
                      showCovidDisclaimerTooltip="if calculated"
                    />
                  </Card>
                </BdTooltip>
                <SectionHeader>
                  Airports with &gt;1% likelihood of use from activity location(s)
                </SectionHeader>
                <ListLabelsHeader
                  lhs={['Source airport']}
                  rhs={['Likelihood of case exportation', 'Estimated case exportations']}
                />
                <List className="xunpadded">
                  {sourceAirports?.map(x => (
                    <List.Item key={x.id}>
                      <BdTooltip
                        className="transparency individual"
                        customPopup={
                          <PopupAirportExport
                            airport={x}
                            totalDestinationVolume={airports?.totalDestinationVolume}
                            totalSourceVolume={airports?.totalSourceVolume}
                            eventStartDate={eventStartDate}
                            appMetadata={appMetadata}
                          />
                        }
                        wide="very"
                        disabled={DisableTRANSPAR}
                      >
                        <AirportExportationItem airport={x} />
                      </BdTooltip>
                    </List.Item>
                  )) || (
                    <Typography
                      variant="body2"
                      color="stone90"
                      sx={{ textAlign: 'center', fontStyle: 'italic' }}
                    >
                      {!exportationRisk ||
                      exportationRisk.isModelNotRun ||
                      exportationRisk.isModelRunning
                        ? 'No airports returned because risk was not calculated'
                        : 'No airports with >1% likelihood of use from activity location(s)'}
                    </Typography>
                  )}
                </List>
              </Accordian>
            )}
            {!!articles.length && (
              <Accordian expanded={false} title="References" yunpadContent sticky>
                <ReferenceList articles={articles} timeframeStartDate={timeframeStartDate} />
                {!!articles.some(a => a.originalLanguage !== 'en') && (
                  <a href="https://translate.google.com/" target="_blank" rel="noopener noreferrer">
                    <img
                      src={GoogleTranslateLogoSvg}
                      alt="translated by Google"
                      sx={{ marginTop: 16, marginBottom: 16 }}
                    />
                  </a>
                )}
              </Accordian>
            )}
          </>
        </ViewPanel>
      )}
      <Routes>
        <Route path="activity" element={<DiseaseActivityUpdates />} />
        <Route path="parameters" element={<ParametersPanel />} />
      </Routes>
    </>
  );
};

export default DiseaseActivityDetails;
